import type { Program } from './schema';

/**
 * When points and miles expire, program by program (rules as checked in October 2026), and the
 * date that follows from the last activity on the account.
 *
 * Most programs expire after a stretch with no activity (earning or spending anything), so the
 * date moves whenever there's a stay, a flight, a redemption, a change in balance or spending
 * on the program's own credit card. Transferable bank points and a few airlines never expire.
 */

export type ExpiryRule =
  | { kind: 'never'; note: string }
  | {
      kind: 'inactivity';
      months: number;
      /** Elite members' points don't expire (IHG, Choice). */
      eliteExempt?: boolean;
      /** Only rentals count (Hertz). */
      rentalsOnly?: boolean;
    }
  | { kind: 'earned'; months: number; note: string };

const NEVER_BANK = 'Never, while the card account is open';

/** The rule for a program, by its id (and name, where one id covers two programs). */
export function expiryRule(programId: string, name = ''): ExpiryRule | null {
  const n = name.toLowerCase();
  if (n.includes('hertz')) return { kind: 'inactivity', months: 12, rentalsOnly: true };
  if (n.includes('rakuten')) return { kind: 'never', note: 'Cash back doesn’t expire' };
  switch (programId) {
    case 'amex_mr':
    case 'chase_ur':
    case 'citi_ty':
    case 'capital_one':
    case 'bilt':
    case 'wells_fargo':
      return { kind: 'never', note: NEVER_BANK };
    case 'delta':
    case 'united':
    case 'southwest':
    case 'jetblue':
    case 'alaska':
      return { kind: 'never', note: 'Miles never expire' };
    case 'virgin':
      return { kind: 'never', note: 'Virgin Points never expire' };
    case 'hilton':
    case 'hyatt':
    case 'marriott':
    case 'american':
    case 'flyingblue':
    case 'wyndham':
      return { kind: 'inactivity', months: programId === 'wyndham' ? 18 : 24 };
    case 'ihg':
      return { kind: 'inactivity', months: 12, eliteExempt: true };
    case 'choice':
      return { kind: 'inactivity', months: 18, eliteExempt: true };
    case 'aeroplan':
      return { kind: 'inactivity', months: 18 };
    case 'avios':
      return { kind: 'inactivity', months: 36 };
    case 'accor':
      return { kind: 'inactivity', months: 12 };
    case 'krisflyer':
      return { kind: 'earned', months: 36, note: 'Each batch expires 3 years after it was earned' };
    case 'emirates':
      return { kind: 'earned', months: 36, note: 'Each batch expires 3 years after it was earned' };
    case 'turkish':
      return { kind: 'earned', months: 36, note: 'Each batch expires 3 years after it was earned' };
    case 'lifemiles':
      // Spending miles doesn't count; earning or moving miles in does.
      return {
        kind: 'earned',
        months: 12,
        note: '12 months after the last miles earned or moved in; redemptions don’t count',
      };
    default:
      return null;
  }
}

/** YYYY-MM-DD plus whole months (Jan 31 + 1 month is Feb 28/29). */
export function addMonths(date: string, months: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const total = y * 12 + (m - 1) + months;
  const year = Math.floor(total / 12);
  const month = (total % 12) + 1;
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${year}-${String(month).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`;
}

const isElite = (tier: string | null | undefined) =>
  !!tier && !/^(club|member|blue|basic|none)$/i.test(tier.trim());

export interface ExpiryResult {
  expiresOn: string | null;
  /** Short reason, shown in the app and to Claude. */
  note: string;
}

/**
 * The expiry date and why, from the rule, the last activity, the tier and any co-brand card
 * that earns into the program (monthly spending keeps it active).
 */
export function computeExpiry(
  p: Pick<Program, 'programId' | 'name' | 'tier' | 'expiresOn'>,
  ctx: { lastActivityOn: string | null; cardName?: string | null },
): ExpiryResult {
  const rule = expiryRule(p.programId, p.name);
  if (!rule) return { expiresOn: p.expiresOn, note: p.expiresOn ? 'From the account' : '' };
  if (rule.kind === 'never') return { expiresOn: null, note: rule.note };
  if (rule.kind === 'earned') return { expiresOn: p.expiresOn, note: rule.note };
  if (rule.eliteExempt && isElite(p.tier))
    return { expiresOn: null, note: `Never while you have ${p.tier} status` };
  if (ctx.cardName && !rule.rentalsOnly)
    return { expiresOn: null, note: `Kept active by your ${ctx.cardName}` };
  const what = rule.rentalsOnly ? 'rental' : 'activity';
  if (!ctx.lastActivityOn)
    return {
      expiresOn: null,
      note: `Expires ${rule.months} months after the last ${what}; last ${what} not known yet`,
    };
  return {
    expiresOn: addMonths(ctx.lastActivityOn, rule.months),
    note: `${rule.months} months after the last ${what} (${ctx.lastActivityOn})`,
  };
}

/** The last activity a stated expiry date implies, for programs that expire after inactivity. */
export function activityFromExpiry(
  p: Pick<Program, 'programId' | 'name'>,
  expiresOn: string,
): string | null {
  const rule = expiryRule(p.programId, p.name);
  return rule?.kind === 'inactivity' ? addMonths(expiresOn, -rule.months) : null;
}
